// ============================================================================
// BOQ — Rate library (benchmark rates) + source documents + vendor create
// ============================================================================
// Library/benchmark rates are boq.rates with project_id NULL: reusable rate
// observations harvested from sample BOQs. Each is tagged with provenance —
// a rate_source_document (filename / date / consultant / default vendor) plus
// a per-rate vendor / date so a single import can mix subcontractor quotes.
//
// This module exposes:
//   useBoqRateLibrary() — list / edit (single + bulk) / delete / bulk-create
//   useBoqRateSources()  — manage the source documents
//   useVendorDirectory() — vendor lookup with create-on-the-fly
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError, normalizeId } from '../../hooks/useSupabase';
import { fetchAllRows } from '../utils/libraryClassification';

const num = (v) => (v === null || v === undefined ? null : Number(v));

const RATE_SELECT = `
  id, item_version_id, project_id, vendor_id, city, rate_date,
  rate_quoted, rate_billed, cost_split, contractor, source, source_filename, source_consultant,
  source_document_id, source_sheet, source_row, source_page, created_at,
  item_versions:item_version_id ( id, full_code, short_description, unit ),
  rate_source_documents:source_document_id ( id, filename, document_date, consultant, storage_path )
`.replace(/\s+/g, ' ');

const mapRate = (r) => ({
  id: r.id,
  itemVersionId: r.item_version_id,
  fullCode: r.item_versions?.full_code || '',
  shortDescription: r.item_versions?.short_description || '',
  unit: r.item_versions?.unit || '',
  projectId: r.project_id,
  vendorId: normalizeId(r.vendor_id),
  city: r.city,
  rateDate: r.rate_date,
  rateQuoted: num(r.rate_quoted),
  rateBilled: num(r.rate_billed),
  supplyRateQuoted: num(r.cost_split?.quoted?.supply),
  installRateQuoted: num(r.cost_split?.quoted?.install),
  supplyRateBilled: num(r.cost_split?.billed?.supply),
  installRateBilled: num(r.cost_split?.billed?.install),
  contractor: r.contractor,
  source: r.source,
  sourceFilename: r.source_filename,
  sourceConsultant: r.source_consultant,
  sourceDocumentId: r.source_document_id,
  // Where in the source file the figure was read: a workbook sheet and row,
  // or a PDF page. Drives the Rates page's source preview.
  sourceSheet: r.source_sheet || null,
  sourceRow: r.source_row ?? null,
  sourcePage: r.source_page ?? null,
  sourceDoc: r.rate_source_documents ? {
    id: r.rate_source_documents.id,
    filename: r.rate_source_documents.filename,
    documentDate: r.rate_source_documents.document_date,
    consultant: r.rate_source_documents.consultant,
    storagePath: r.rate_source_documents.storage_path || null,
  } : null,
  createdAt: r.created_at,
});

// Map an editable patch (camelCase) to rate column updates.
function ratePatchToPayload(patch) {
  const p = {};
  if (patch.vendorId !== undefined) p.vendor_id = patch.vendorId || null;
  if (patch.city !== undefined) p.city = patch.city?.trim() || null;
  if (patch.rateDate !== undefined) p.rate_date = patch.rateDate || null;
  if (patch.rateQuoted !== undefined) p.rate_quoted = (patch.rateQuoted === '' || patch.rateQuoted == null) ? null : Number(patch.rateQuoted);
  if (patch.rateBilled !== undefined) p.rate_billed = (patch.rateBilled === '' || patch.rateBilled == null) ? null : Number(patch.rateBilled);
  if (patch.costSplit !== undefined) p.cost_split = patch.costSplit || null;
  if (patch.contractor !== undefined) p.contractor = patch.contractor?.trim() || null;
  if (patch.sourceConsultant !== undefined) p.source_consultant = patch.sourceConsultant?.trim() || null;
  if (patch.sourceDocumentId !== undefined) p.source_document_id = patch.sourceDocumentId || null;
  return p;
}

export const useBoqRateLibrary = ({ enabled = true } = {}) => {
  const [rates, setRates] = useState([]);
  // Start in the loading state: the hook fetches on mount (or as soon as it is
  // enabled), so a `false` start would flash the "no rates" empty state before
  // the fetch begins.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Paged: the library holds more rates than PostgREST's 1000-row cap, and a
      // plain select silently dropped the oldest (whole imports went missing).
      // id breaks the created_at tie, since one import shares a timestamp and
      // .range() needs a stable order to not skip or repeat rows across pages.
      const data = await fetchAllRows(() => supabase
        .schema('boq').from('rates').select(RATE_SELECT)
        .is('project_id', null)
        .order('created_at', { ascending: false })
        .order('id'));
      setRates(data.map(mapRate));
    } catch (err) {
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'rates' }));
    } finally { setLoading(false); }
  }, []);

  const updateRate = useCallback(async (id, patch) => {
    try {
      const { data, error: uErr } = await supabase.schema('boq').from('rates')
        .update(ratePatchToPayload(patch)).eq('id', id).select(RATE_SELECT).single();
      if (uErr) throw uErr;
      setRates((prev) => prev.map((r) => (r.id === id ? mapRate(data) : r)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'rate' }) };
    }
  }, []);

  const bulkUpdate = useCallback(async (ids, patch) => {
    if (!ids?.length) return { success: true, count: 0 };
    try {
      const { data, error: uErr } = await supabase.schema('boq').from('rates')
        .update(ratePatchToPayload(patch)).in('id', ids).select(RATE_SELECT);
      if (uErr) throw uErr;
      const byId = new Map((data || []).map((r) => [r.id, mapRate(r)]));
      setRates((prev) => prev.map((r) => byId.get(r.id) || r));
      return { success: true, count: byId.size };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'rates' }) };
    }
  }, []);

  const deleteMany = useCallback(async (ids) => {
    if (!ids?.length) return { success: true };
    try {
      const { error: dErr } = await supabase.schema('boq').from('rates').delete().in('id', ids);
      if (dErr) throw dErr;
      const idSet = new Set(ids);
      setRates((prev) => prev.filter((r) => !idSet.has(r.id)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'rates' }) };
    }
  }, []);

  // Bulk-insert library rates (used by manual add and import). Each row needs
  // item_version_id; project_id stays NULL (benchmark).
  const createRates = useCallback(async (rows) => {
    if (!rows?.length) return { success: true, count: 0 };
    try {
      const { data: sessionData, error: sErr } = await supabase.auth.getSession();
      if (sErr || !sessionData?.session) return { success: false, error: 'Please sign in.' };
      const payload = rows.map((row) => ({
        item_version_id: row.itemVersionId,
        project_id: null,
        vendor_id: row.vendorId || null,
        city: row.city?.trim() || null,
        rate_date: row.rateDate || new Date().toISOString().slice(0, 10),
        rate_quoted: (row.rateQuoted === '' || row.rateQuoted == null) ? null : Number(row.rateQuoted),
        rate_billed: (row.rateBilled === '' || row.rateBilled == null) ? null : Number(row.rateBilled),
        contractor: row.contractor?.trim() || null,
        source: row.source || 'import',
        source_filename: row.sourceFilename?.trim() || null,
        source_consultant: row.sourceConsultant?.trim() || null,
        source_document_id: row.sourceDocumentId || null,
      }));
      const { data, error: insErr } = await supabase.schema('boq').from('rates').insert(payload).select(RATE_SELECT);
      if (insErr) throw insErr;
      const mapped = (data || []).map(mapRate);
      setRates((prev) => [...mapped, ...prev]);
      return { success: true, count: mapped.length, rates: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'rates' }) };
    }
  }, []);

  // Custom (manual) rates go through boq.add_custom_rate, which is narrower
  // than rates_write: curators plus a named allow-list (boq.can_add_custom_rate).
  // canAddCustom mirrors that gate so the page hides what the RPC would refuse.
  const [canAddCustom, setCanAddCustom] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    supabase.schema('boq').rpc('can_add_custom_rate')
      .then(({ data, error: gErr }) => setCanAddCustom(!gErr && data === true));
  }, [enabled]);

  const addCustomRate = useCallback(async (input) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return { success: false, error: 'Please sign in.' };
      const toNumOrNull = (v) => (v === '' || v == null ? null : Number(v));
      const { data: id, error: rpcErr } = await supabase.schema('boq').rpc('add_custom_rate', {
        p_item_version_id: input.itemVersionId,
        p_basis: input.basis,
        p_rate: toNumOrNull(input.rate),
        p_supply: toNumOrNull(input.supply),
        p_install: toNumOrNull(input.install),
        p_rate_date: input.rateDate || null,
        p_consultant: input.consultant || null,
        p_contractor: input.contractor || null,
        p_vendor_id: input.vendorId || null,
        p_city: input.city || null,
      });
      if (rpcErr) throw rpcErr;
      const { data: row, error: fErr } = await supabase.schema('boq').from('rates').select(RATE_SELECT).eq('id', id).single();
      if (fErr) throw fErr;
      const mapped = mapRate(row);
      setRates((prev) => [mapped, ...prev]);
      return { success: true, rate: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'rate' }) };
    }
  }, []);

  // Deletes custom (manual) rates only: boq.delete_custom_rates ignores any
  // imported rate id it is handed, so the count says what actually went.
  const deleteCustomRates = useCallback(async (ids) => {
    if (!ids?.length) return { success: true, count: 0 };
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) return { success: false, error: 'Please sign in.' };
      const { data: n, error: rpcErr } = await supabase.schema('boq').rpc('delete_custom_rates', { p_ids: ids });
      if (rpcErr) throw rpcErr;
      if (!n) return { success: false, error: 'Nothing was deleted. Only custom rates can be removed here.' };
      const gone = new Set(ids);
      setRates((prev) => prev.filter((r) => !(gone.has(r.id) && r.source === 'manual')));
      return { success: true, count: Number(n) };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'rate' }) };
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { rates, loading, error, refresh: fetchAll, updateRate, bulkUpdate, deleteMany, createRates, canAddCustom, addCustomRate, deleteCustomRates };
};

// ─── Source documents ─────────────────────────────────────────────────────────
const mapSource = (s) => ({
  id: s.id,
  filename: s.filename,
  documentDate: s.document_date,
  consultant: s.consultant,
  defaultVendorId: normalizeId(s.default_vendor_id),
  city: s.city,
  currency: s.currency,
  note: s.note,
});

export const useBoqRateSources = ({ enabled = true } = {}) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.schema('boq').from('rate_source_documents')
        .select('id, filename, document_date, consultant, default_vendor_id, city, currency, note')
        .order('document_date', { ascending: false });
      if (!error) setSources((data || []).map(mapSource));
    } finally { setLoading(false); }
  }, []);

  const createSource = useCallback(async (input) => {
    try {
      const { data, error } = await supabase.schema('boq').from('rate_source_documents')
        .insert({
          filename: input.filename?.trim() || null,
          document_date: input.documentDate,
          consultant: input.consultant?.trim() || null,
          default_vendor_id: input.defaultVendorId || null,
          city: input.city?.trim() || null,
          currency: input.currency || null,
          note: input.note?.trim() || null,
        })
        .select('id, filename, document_date, consultant, default_vendor_id, city, currency, note').single();
      if (error) throw error;
      const mapped = mapSource(data);
      setSources((prev) => [mapped, ...prev]);
      return { success: true, source: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'source document' }) };
    }
  }, []);

  const updateSource = useCallback(async (id, patch) => {
    try {
      const payload = {};
      if (patch.filename !== undefined) payload.filename = patch.filename?.trim() || null;
      if (patch.documentDate !== undefined) payload.document_date = patch.documentDate;
      if (patch.consultant !== undefined) payload.consultant = patch.consultant?.trim() || null;
      if (patch.defaultVendorId !== undefined) payload.default_vendor_id = patch.defaultVendorId || null;
      if (patch.city !== undefined) payload.city = patch.city?.trim() || null;
      if (patch.currency !== undefined) payload.currency = patch.currency || null;
      if (patch.note !== undefined) payload.note = patch.note?.trim() || null;
      const { data, error } = await supabase.schema('boq').from('rate_source_documents')
        .update(payload).eq('id', id)
        .select('id, filename, document_date, consultant, default_vendor_id, city, currency, note').single();
      if (error) throw error;
      setSources((prev) => prev.map((s) => (s.id === id ? mapSource(data) : s)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'source document' }) };
    }
  }, []);

  const deleteSource = useCallback(async (id) => {
    try {
      const { error } = await supabase.schema('boq').from('rate_source_documents').delete().eq('id', id);
      if (error) throw error;
      setSources((prev) => prev.filter((s) => s.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'source document' }) };
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { sources, loading, refresh: fetchAll, createSource, updateSource, deleteSource };
};

// ─── Vendor directory (lookup + create-on-the-fly) ──────────────────────────────
export const useVendorDirectory = ({ enabled = true } = {}) => {
  const [vendors, setVendors] = useState([]);

  const fetchAll = useCallback(async () => {
    const { data, error } = await supabase.from('vendors').select('id, company_name').order('company_name');
    if (!error) setVendors((data || []).map((v) => ({ id: normalizeId(v.id), name: v.company_name || '(unnamed)' })));
  }, []);

  // Create a vendor in the contacts directory if one with this name doesn't
  // already exist; returns its id either way.
  const findOrCreateVendor = useCallback(async (name, extra = {}) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { success: false, error: 'Vendor name required.' };
    const existing = vendors.find((v) => v.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) return { success: true, id: existing.id, created: false };
    try {
      const { data, error } = await supabase.from('vendors')
        .insert({ company_name: trimmed, category: extra.category || 'Contractor', location: extra.location || '' })
        .select('id, company_name').single();
      if (error) throw error;
      const v = { id: normalizeId(data.id), name: data.company_name };
      setVendors((prev) => [...prev, v].sort((a, b) => a.name.localeCompare(b.name)));
      return { success: true, id: v.id, created: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'vendor' }) };
    }
  }, [vendors]);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { vendors, refresh: fetchAll, findOrCreateVendor };
};
